<template>
  <div v-if="title && introduction" :id="termSlug" class="glossary-term">
    <a
      :href="termLink"
      aria-label="Copy link to the clipboard"
      rel="nofollow"
      title="Copy link to the clipboard"
      @click.prevent="copyLink()"
    >
      <h2>
        {{ title }}
        <i class="icon icon--lg">
          <Icon name="fa6-solid:link" />
        </i>
      </h2>
      <transition
        name="tooltip"
        @after-enter="transitionAfterEnter"
        @before-enter="transitionBeforeEnter"
      >
        <div v-if="hasTooltip" class="tooltip">
          {{ tooltipMessage }}
        </div>
      </transition>
    </a>
    <RichTextRenderer :document="introduction" />
  </div>
</template>

<script setup lang="ts">
import type { GlossaryTermStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: GlossaryTermStoryblok
}>()
const storyContent = computed(() => props.blok?.content)
const { title, introduction } = useStoryContent({
  ...toRefs(props),
  storyContent,
})
const route = useRoute()
const hasTooltip = ref(false)
const tooltipMessage = ref('')
const tooltipSuccess = ref('Successfully copied')
const tooltipError = ref('Something went wrong')
const termSlug = computed(() => props.blok.slug)
const termLink = computed(() => route.path + '#' + termSlug.value)

const copyLink = () => {
  navigator.clipboard
    .writeText(termLink.value)
    .then(() => {
      tooltipMessage.value = tooltipSuccess.value
    })
    .catch(() => {
      tooltipMessage.value = tooltipError.value
    })
    .finally(() => {
      hasTooltip.value = true
    })
}

const transitionBeforeEnter = (el) => {
  el.style.opacity = 1
}

const transitionAfterEnter = (el) => {
  el.style.opacity = 0
  el.style.transform = 'translateY(-150%)'
  hasTooltip.value = false
}
</script>

<style lang="less" scoped>
.glossary-term {
  > a {
    .inline-block();
    .relative();

    color: @color-text;

    .tooltip {
      .absolute();
      .rounded-sm();

      top: 0;
      left: 0;
      transform: translateY(-100%);
      color: @color-body;
      padding: @gutter-xs @gutter;
      white-space: nowrap;
      transition: all 0.5s ease-in;
      background: fade(@color-fill-dark, 85%);
    }
  }
  i {
    color: @color-secondary;
    padding-left: @gutter-sm;
    line-height: inherit;
  }
  &:not(:last-child) {
    margin-bottom: @gutter-xl;
  }
}
</style>
